import { useCallback, useEffect, useRef } from 'react';

/**
 * Keeps a scrollable element usable when it is rendered in a portal that sits
 * outside a dialog.
 *
 * A dialog locks page scrolling by cancelling wheel and touch events that reach
 * `document` from anywhere outside its own content. A popover portals to
 * `document.body`, so its scroll areas are treated as "outside" and stop
 * scrolling. Stopping the events on the element keeps them from reaching
 * `document` while leaving the element's native scrolling intact.
 */
export function useOverlayScrollable<T extends HTMLElement>() {
  const nodeRef = useRef<T | null>(null);
  const detachRef = useRef<(() => void) | null>(null);

  const ref = useCallback((node: T | null) => {
    detachRef.current?.();
    detachRef.current = null;
    nodeRef.current = node;
    if (!node) return;

    const stopPropagation = (event: Event) => event.stopPropagation();
    node.addEventListener('wheel', stopPropagation, { passive: false });
    node.addEventListener('touchmove', stopPropagation, { passive: false });
    detachRef.current = () => {
      node.removeEventListener('wheel', stopPropagation);
      node.removeEventListener('touchmove', stopPropagation);
    };
  }, []);

  useEffect(() => () => detachRef.current?.(), []);

  return { ref, nodeRef };
}
